import { useState } from 'react';
import { Button, Input, cn } from './ui';

/**
 * Kurze Tags eingeben - tippen, Enter, fertig.
 *
 * Bekannte Tags werden als Vorschlag angeboten, neue entstehen einfach durch
 * Eintippen. Keine Liste, die vorher gepflegt werden muss.
 */
export function TagPicker({
  id,
  value,
  onChange,
  suggestions = [],
  placeholder = 'Tag eintippen …',
  maxTags = 10,
  maxLength = 30,
  disabled,
}: {
  id?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  maxTags?: number;
  maxLength?: number;
  disabled?: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState('');

  const add = (tag: string): void => {
    const clean = tag.trim().slice(0, maxLength);
    if (!clean || value.length >= maxTags) return;
    if (!value.some((existing) => existing.toLocaleLowerCase('de') === clean.toLocaleLowerCase('de'))) {
      onChange([...value, clean]);
    }
    setDraft('');
  };

  const offen = suggestions
    .filter((tag) => !value.some((existing) => existing.toLocaleLowerCase('de') === tag.toLocaleLowerCase('de')))
    .filter((tag) => !draft.trim() || tag.toLocaleLowerCase('de').includes(draft.trim().toLocaleLowerCase('de')))
    .slice(0, 8);

  return (
    <div className="space-y-1">
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {value.map((tag) => (
            <li key={tag}>
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-200">
                {tag}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`${tag} entfernen`}
                    className="text-brand-700 hover:text-rose-600"
                    onClick={() => onChange(value.filter((existing) => existing !== tag))}
                  >
                    ×
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!disabled && value.length < maxTags && (
        <div className="flex gap-1">
          <Input
            id={id}
            value={draft}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                add(draft);
              }
              if (event.key === 'Backspace' && !draft && value.length > 0) onChange(value.slice(0, -1));
            }}
            className="min-w-0 flex-1"
          />
          <Button type="button" size="sm" variant="secondary" disabled={!draft.trim()} onClick={() => add(draft)}>
            +
          </Button>
        </div>
      )}
      {!disabled && offen.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {offen.map((tag) => (
            <button
              key={tag}
              type="button"
              className={cn('rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-200')}
              onClick={() => add(tag)}
            >
              + {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Tags nur anzeigen, z. B. in einer Kaderzeile. */
export function TagList({ tags, className }: { tags: string[]; className?: string }): JSX.Element | null {
  if (tags.length === 0) return null;
  return (
    <span className={cn('inline-flex flex-wrap gap-1', className)}>
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600"
        >
          {tag}
        </span>
      ))}
    </span>
  );
}
